import { api } from './api';
import { TeamInformation, CreateTeamMemberData } from '../types';
import { networkService } from './network';

/**
 * Team Information Service
 * Handles database persistence for team member assignments on requests
 */
export class TeamInformationService {

  /**
   * Cache for partner contributor access levels
   * Public to allow permission service to check cache synchronously
   */
  public accessLevelCache: Map<string, string> = new Map();

  /**
   * In-flight promises for access level fetches to prevent duplicate concurrent requests
   * Private to ensure proper deduplication
   */
  private accessLevelPromises: Map<string, Promise<'read' | 'read_write' | undefined>> = new Map();

  /**
   * Get all team members for a specific request
   */
  async getTeamMembers(requestId: string): Promise<TeamInformation[]> {
    return api.get<TeamInformation[]>(`/requests/${requestId}/team`);
  }

  /**
   * Get partner contributor access level for a specific user on a request
   * Uses caching and promise deduplication to avoid repeated API calls
   */
  async getPartnerContributorAccessLevel(requestId: string, userEmail: string): Promise<'read' | 'read_write' | undefined> {
    const cacheKey = `${requestId}:${userEmail}`;

    // Check cache first
    if (this.accessLevelCache.has(cacheKey)) {
      return this.accessLevelCache.get(cacheKey) as 'read' | 'read_write' | undefined;
    }

    // Skip API call for offline requests (o-XXX) - they haven't been synced yet
    if (requestId.startsWith('o-')) {
      return undefined;
    }

    // Skip API call when offline - return undefined to prevent network errors
    if (!networkService.isConnected()) {
      return undefined;
    }

    // Check if there's already an in-flight request for this key
    if (this.accessLevelPromises.has(cacheKey)) {
      console.log(`⏳ Waiting for existing access level fetch for ${cacheKey}`);
      return this.accessLevelPromises.get(cacheKey)!;
    }

    // Create new promise for this fetch
    const fetchPromise = this.fetchAccessLevel(requestId, userEmail, cacheKey);
    this.accessLevelPromises.set(cacheKey, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      // Clean up the in-flight promise
      this.accessLevelPromises.delete(cacheKey);
    }
  }

  /**
   * Internal method to fetch access level from API
   */
  private async fetchAccessLevel(requestId: string, userEmail: string, cacheKey: string): Promise<'read' | 'read_write' | undefined> {
    try {
      const teamMembers = await this.getTeamMembers(requestId);
      const partnerMember = teamMembers.find(
        member => member.userRole === 'partner_contributor' && member.userEmail === userEmail
      );

      const accessLevel = partnerMember?.accessLevel || undefined;

      // ALWAYS cache the result, even if undefined
      // This prevents repeated API calls for users who are not partner contributors
      this.accessLevelCache.set(cacheKey, accessLevel as any);

      return accessLevel as 'read' | 'read_write' | undefined;
    } catch (error) {
      console.error(`Failed to fetch partner contributor access level for ${userEmail} on request ${requestId}:`, error);
      // Cache undefined on error to prevent repeated failures
      this.accessLevelCache.set(cacheKey, undefined as any);
      return undefined;
    }
  }

  /**
   * Clear access level cache for a specific request or all requests
   */
  clearAccessLevelCache(requestId?: string): void {
    if (requestId) {
      // Clear all cache entries for this request
      const keysToDelete: string[] = [];
      this.accessLevelCache.forEach((_, key) => {
        if (key.startsWith(`${requestId}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.accessLevelCache.delete(key));
    } else {
      // Clear entire cache
      this.accessLevelCache.clear();
    }
  }

  /**
   * Get team members by role for a specific request
   */
  async getTeamMembersByRole(
    requestId: string,
    role: 'vertiv_sponsor' | 'vertiv_contributor' | 'partner_contributor'
  ): Promise<TeamInformation[]> {
    return api.get<TeamInformation[]>(`/requests/${requestId}/team/role/${role}`);
  }

  /**
   * Add team members to a request
   */
  async addTeamMembers(requestId: string, teamMembers: CreateTeamMemberData[]): Promise<TeamInformation[]> {
    return api.post<TeamInformation[]>(`/requests/${requestId}/team`, teamMembers);
  }

  /**
   * Replace all team members for a request
   */
  async replaceTeamMembers(requestId: string, teamMembers: CreateTeamMemberData[]): Promise<TeamInformation[]> {
    return api.put<TeamInformation[]>(`/requests/${requestId}/team`, teamMembers);
  }

  /**
   * Delete a specific team member
   */
  async deleteTeamMember(requestId: string, teamId: number): Promise<void> {
    return api.delete(`/requests/${requestId}/team/${teamId}`);
  }

  /**
   * Delete all team members for a request
   */
  async deleteAllTeamMembers(requestId: string): Promise<void> {
    return api.delete(`/requests/${requestId}/team`);
  }

  /**
   * Helper method to organize team members by role
   * ✅ PHASE 4 FIX: Include Default Sponsor roles in vertivSponsors array
   */
  organizeTeamMembersByRole(teamMembers: TeamInformation[]): {
    vertivSponsors: TeamInformation[];
    vertivContributors: TeamInformation[];
    partnerContributors: TeamInformation[];
  } {
    return {
      // Include both regular Vertiv Sponsors and Default Sponsors (NAM, EMEA, APAC)
      vertivSponsors: teamMembers.filter(member =>
        member.userRole === 'vertiv_sponsor' ||
        member.userRole === 'default_sponsor_nam' ||
        member.userRole === 'default_sponsor_emea' ||
        member.userRole === 'default_sponsor_apac'
      ),
      vertivContributors: teamMembers.filter(member => member.userRole === 'vertiv_contributor'),
      partnerContributors: teamMembers.filter(member => member.userRole === 'partner_contributor')
    };
  }

  /**
   * Helper method to convert from legacy team member format
   */
  convertFromLegacyFormat(
    vertivSponsors: string[] | any,
    vertivContributors: string[] | any,
    partnerContributors: string[] | any,
    allUsers: Array<{ id: string; name: string; email: string }>
  ): CreateTeamMemberData[] {
    const teamMembers: CreateTeamMemberData[] = [];

    // Helper function to ensure we have a valid array
    const ensureArray = (value: any): string[] => {
      if (Array.isArray(value)) {
        return value.filter(item => typeof item === 'string');
      }
      if (typeof value === 'string') {
        return [value];
      }
      return [];
    };

    // Convert Vertiv Sponsors
    const sponsorsArray = ensureArray(vertivSponsors);
    sponsorsArray.forEach(userId => {
      const user = allUsers.find(u => u.id === userId);
      if (user) {
        teamMembers.push({
          userEmail: user.email,
          userFullName: user.name,
          userRole: 'vertiv_sponsor'
        });
      }
    });

    // Convert Vertiv Contributors
    const contributorsArray = ensureArray(vertivContributors);
    contributorsArray.forEach(userId => {
      const user = allUsers.find(u => u.id === userId);
      if (user) {
        teamMembers.push({
          userEmail: user.email,
          userFullName: user.name,
          userRole: 'vertiv_contributor'
        });
      }
    });

    // Convert Partner Contributors
    const partnersArray = ensureArray(partnerContributors);
    partnersArray.forEach(userId => {
      const user = allUsers.find(u => u.id === userId);
      if (user) {
        teamMembers.push({
          userEmail: user.email,
          userFullName: user.name,
          userRole: 'partner_contributor'
        });
      }
    });

    return teamMembers;
  }

  /**
   * Helper method to convert to legacy team member format (for backward compatibility)
   */
  convertToLegacyFormat(
    teamMembers: TeamInformation[],
    allUsers: Array<{ id: string; name: string; email: string }>
  ): {
    vertivSponsors: string[];
    vertivContributors: string[];
    partnerContributors: string[];
  } {
    const organized = this.organizeTeamMembersByRole(teamMembers);

    const findUserIdByEmail = (email: string): string | undefined => {
      // Case-insensitive email comparison (auto-added sponsors may have different casing)
      const normalizedEmail = email?.toLowerCase();
      return allUsers.find(user => user.email?.toLowerCase() === normalizedEmail)?.id;
    };

    return {
      vertivSponsors: organized.vertivSponsors
        .map(member => findUserIdByEmail(member.userEmail))
        .filter((id): id is string => !!id),
      vertivContributors: organized.vertivContributors
        .map(member => findUserIdByEmail(member.userEmail))
        .filter((id): id is string => !!id),
      partnerContributors: organized.partnerContributors
        .map(member => findUserIdByEmail(member.userEmail))
        .filter((id): id is string => !!id)
    };
  }

  /**
   * Get partner contributors with access levels from team members
   */
  getPartnerContributorsWithAccessLevels(teamMembers: TeamInformation[]): Array<{ email: string; accessLevel: 'read' | 'read_write' }> {
    const organized = this.organizeTeamMembersByRole(teamMembers);

    return organized.partnerContributors.map(member => ({
      email: member.userEmail,
      accessLevel: member.accessLevel || 'read'
    }));
  }
}

export const teamInformationService = new TeamInformationService();